/**
 * Server-sent events over POST.
 *
 * `EventSource` can only issue GETs, and both streams here need a JSON body
 * (a 14 kB prompt, an image id list). So this parses the SSE framing off a
 * streamed fetch response instead — same wire format, same server code.
 */

import { ApiError } from './client';

const BASE = import.meta.env.VITE_API_BASE || '/api/v1';

/**
 * @param {string} path
 * @param {object} body
 * @param {{onEvent: (name: string, data: any) => void, signal?: AbortSignal}} handlers
 */
export async function streamEvents(path, body, { onEvent, signal }) {
  const response = await fetch(`${BASE}${path}`, {
    // Same reason as the main client: the session cookie is HttpOnly and would
    // otherwise be omitted on a cross-origin dev request, so the stream 401s.
    credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      /* ignore */
    }
    const fallbackCode = response.status === 429 ? 'capacity_exceeded' : `http_${response.status}`;
    throw new ApiError(payload?.error?.message || `Stream failed (HTTP ${response.status})`, {
      code: payload?.error?.code || fallbackCode,
      status: response.status,
      details: payload?.error?.details || {},
      retryAfter: Number(response.headers.get('Retry-After')) || 0,
    });
  }
  if (!response.body) throw new ApiError('Streaming is not supported by this browser');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Frames are separated by a blank line; keep any partial tail in the buffer.
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      dispatch(frame, onEvent);
      boundary = buffer.indexOf('\n\n');
    }
  }
  if (buffer.trim()) dispatch(buffer, onEvent);
}

function dispatch(frame, onEvent) {
  let name = 'message';
  const dataLines = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // keep-alive comment
    if (line.startsWith('event:')) name = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  if (!dataLines.length) return;
  const raw = dataLines.join('\n');
  try {
    onEvent(name, JSON.parse(raw));
  } catch {
    onEvent(name, raw);
  }
}

/** GET-based SSE (batch progress), where EventSource is the simpler tool. */
export function subscribeEvents(path, { onEvent, onError } = {}) {
  // withCredentials so the session cookie reaches a cross-origin API in dev.
  const source = new EventSource(`${BASE}${path}`, { withCredentials: true });
  const forward = (name) => (event) => {
    try {
      onEvent?.(name, JSON.parse(event.data));
    } catch {
      onEvent?.(name, event.data);
    }
  };
  ['progress', 'done', 'error'].forEach((name) => source.addEventListener(name, forward(name)));
  source.onerror = () => {
    onError?.(new ApiError('Progress stream disconnected', { code: 'stream_disconnected' }));
    source.close();
  };
  return () => source.close();
}
